import { useState } from 'react';
import { swapStep } from '../data';

export default function SwapStepSection() {
  const [swapRate, setSwapRate] = useState(-4.50);
  const [lotSize, setLotSize] = useState(1.0);
  const [daysHeld, setDaysHeld] = useState(7);
  const [hasTriple, setHasTriple] = useState(false);

  const swapCost = swapRate * lotSize * daysHeld;
  const isPositive = swapCost >= 0;

  const presets = [
    { label: 'EURUSD Long 7d', rate: -4.50, lots: 1.0, days: 7, triple: false },
    { label: 'EURUSD Long 30d', rate: -4.50, lots: 1.0, days: 30, triple: false },
    { label: 'EURUSD Short 7d', rate: 1.20, lots: 1.0, days: 7, triple: false },
    { label: 'USDTRY Micro 14d', rate: -3.50, lots: 0.1, days: 14, triple: false },
    { label: 'Wed Rollover', rate: -4.50, lots: 1.0, days: 3, triple: true },
  ];

  return (
    <section id="step-3-swap" className="relative w-full bg-background-50 overflow-hidden">
      <div className="w-full max-w-main mx-auto px-4 md:px-6 py-12 md:py-18">

        {/* --- STEP HEADER --- */}
        <div className="mb-8 md:mb-14">
          <h2 className="text-[28px] md:text-[52px] font-[750] leading-[1.02] text-foreground-950 tracking-[-0.03em] max-w-[780px]">
            {swapStep.title}
          </h2>
        </div>

        {/* --- MAIN GRID --- */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_440px] gap-8 lg:gap-12">

          {/* ========== LEFT — Editorial ========== */}
          <div>

            {/* Formula as large editorial typography */}
            <div className="mb-8 md:mb-10">
              <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-foreground-400 mb-4 md:mb-5">
                The Formula
              </div>

              <div className="space-y-2 md:space-y-3">
                {/* Line 1: Swap Cost = */}
                <div className="flex items-baseline gap-2 md:gap-3 flex-wrap">
                  <span className="text-[28px] md:text-[44px] font-[750] text-foreground-950 tracking-[-0.02em] leading-none">
                    Swap Cost
                  </span>
                  <span className="text-[22px] md:text-[32px] font-[200] text-foreground-400 leading-none">
                    =
                  </span>
                </div>

                {/* Line 2: Rate × Lot Size × Days Held */}
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="text-[26px] md:text-[40px] font-[750] text-secondary-500 tracking-[-0.02em] leading-none">
                    Rate
                  </span>
                  <span className="text-[18px] md:text-[22px] font-[300] text-foreground-400 leading-none">×</span>
                  <span className="text-[26px] md:text-[40px] font-[750] text-foreground-950 tracking-[-0.02em] leading-none">
                    Lot Size
                  </span>
                  <span className="text-[18px] md:text-[22px] font-[300] text-foreground-400 leading-none">×</span>
                  <span className="text-[26px] md:text-[40px] font-[750] text-foreground-950 tracking-[-0.02em] leading-none">
                    Days Held
                  </span>
                </div>
              </div>

              {/* Tagline below formula */}
              <p className="text-[13px] md:text-[14px] text-foreground-500 mt-4 md:mt-5 max-w-[560px] leading-relaxed">
                {swapStep.tagline}
              </p>
            </div>

            {/* Divider */}
            <div className="w-full h-px bg-foreground-200/60 mb-8 md:mb-10" />

            {/* Breakdown — terminal data rows */}
            <div className="mb-8 md:mb-10 bg-foreground-950 rounded-2xl overflow-hidden">
              {/* Terminal header bar */}
              <div className="flex items-center gap-2 px-4 md:px-5 py-3 border-b border-foreground-800/40">
                <div className="flex gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-foreground-600" />
                  <div className="w-2.5 h-2.5 rounded-full bg-foreground-600" />
                  <div className="w-2.5 h-2.5 rounded-full bg-foreground-600" />
                </div>
                <span className="text-[11px] font-[600] text-foreground-600 uppercase tracking-[0.08em] ml-2">formula — variables</span>
              </div>

              {/* Data rows — terminal output style */}
              <div className="divide-y divide-foreground-800/30">
                {swapStep.breakdown.map((item, i) => {
                  const accentColors = [
                    'text-secondary-400 border-secondary-500/30',
                    'text-foreground-200 border-foreground-600/30',
                    'text-foreground-200 border-foreground-600/30',
                  ];
                  return (
                    <div key={item.symbol} className="flex items-start gap-3 md:gap-5 px-4 md:px-5 py-4 md:py-5 group">
                      {/* Left — variable name, monospace */}
                      <div className="w-[80px] sm:w-[130px] flex-shrink-0 pt-0.5">
                        <div className="text-[10px] md:text-[11px] font-[600] text-foreground-500 uppercase tracking-[0.06em] mb-1.5">
                          var {String(i + 1).padStart(2, '0')}
                        </div>
                        <code className={`text-[12px] sm:text-[16px] font-[700] font-mono break-words leading-snug ${accentColors[i]}`}>
                          {item.symbol}
                        </code>
                      </div>

                      {/* Right — structured description */}
                      <div className="flex-1 min-w-0">
                        <div className="text-[13px] md:text-[14px] font-[600] text-foreground-100 mb-1">
                          {item.label}
                        </div>
                        <p className="text-[13px] md:text-[14px] leading-relaxed text-foreground-400 max-w-[520px]">
                          {item.desc}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Triple swap callout */}
            <div className="mb-8 md:mb-10 border-l-2 border-secondary-500/40 pl-5 md:pl-6 py-0.5">
              <div className="text-[11px] font-[700] uppercase tracking-[0.15em] text-secondary-500/60 mb-3">
                Wednesday Rollover
              </div>
              <p className="text-[14px] md:text-[15px] leading-relaxed text-foreground-700 max-w-[560px]">
                {swapStep.tripleSwapNote}
              </p>
            </div>

            {/* Insight */}
            <div className="border-l-2 border-secondary-500/40 pl-5 md:pl-6 py-0.5">
              <div className="text-[11px] font-[700] uppercase tracking-[0.15em] text-secondary-500/60 mb-3">
                Key Insight
              </div>
              <p className="text-[14px] md:text-[15px] leading-relaxed text-foreground-700 max-w-[560px]">
                {swapStep.insight}
              </p>
            </div>
          </div>

          {/* ========== RIGHT — Dashboard Calculator ========== */}
          <div className="relative">
            {/* Dashboard panel */}
            <div className="lg:sticky lg:top-24 bg-foreground-950 rounded-2xl overflow-hidden">

              {/* Panel header bar */}
              <div className="flex items-center justify-between px-5 py-3 border-b border-foreground-800/40">
                <div className="flex items-center gap-2">
                  <div className="flex gap-1.5">
                    <div className="w-2 h-2 rounded-full bg-foreground-600" />
                    <div className="w-2 h-2 rounded-full bg-foreground-600" />
                    <div className="w-2 h-2 rounded-full bg-foreground-600" />
                  </div>
                  <span className="text-[12px] font-[600] text-foreground-400 uppercase tracking-[0.08em] ml-2.5">
                    Live Swap Calculator
                  </span>
                </div>
                <span className="text-[10px] text-foreground-600 font-mono">v2.0</span>
              </div>

              {/* Panel body */}
              <div className="p-5 md:p-6 space-y-6">

                {/* Presets */}
                <div className="grid grid-cols-2 gap-1.5">
                  {presets.map((p, i) => {
                    const isActive = swapRate === p.rate && lotSize === p.lots && daysHeld === p.days && hasTriple === p.triple;
                    return (
                      <button
                        key={i}
                        onClick={() => { setSwapRate(p.rate); setLotSize(p.lots); setDaysHeld(p.days); setHasTriple(p.triple); }}
                        className={`text-left px-3.5 py-2.5 rounded-lg border transition-all duration-200 cursor-pointer ${
                          isActive
                            ? 'border-secondary-500/50 bg-secondary-500/8 ring-1 ring-secondary-500/20'
                            : 'border-foreground-800/30 bg-foreground-900/30 hover:border-foreground-700/40 hover:bg-foreground-900/50'
                        }`}
                      >
                        <div className={`text-[13px] font-[600] leading-snug whitespace-nowrap ${isActive ? 'text-secondary-400' : 'text-foreground-200'}`}>
                          {p.label}
                        </div>
                        <div className={`text-[11px] mt-1 font-mono ${isActive ? 'text-secondary-500/50' : 'text-foreground-600'}`}>
                          {p.rate >= 0 ? '+' : ''}{p.rate.toFixed(2)}/d · {p.lots}L
                        </div>
                      </button>
                    );
                  })}
                </div>

                {/* Separator */}
                <div className="w-full h-px bg-foreground-800/30" />

                {/* Sliders */}
                <div className="space-y-5">
                  {/* Swap Rate slider */}
                  <div>
                    <div className="flex items-center justify-between mb-2.5">
                      <span className="text-[11px] font-[700] text-foreground-300 uppercase tracking-[0.08em]">Rate</span>
                      <div className="flex items-baseline gap-1">
                        <span className={`text-[20px] font-[750] leading-none font-mono tracking-tight ${swapRate >= 0 ? 'text-accent-400' : 'text-secondary-400'}`}>
                          {swapRate >= 0 ? '+' : ''}{swapRate.toFixed(2)}
                        </span>
                        <span className="text-[11px] text-foreground-500 font-mono">$/lot/d</span>
                      </div>
                    </div>
                    <div className="relative h-[22px] flex items-center">
                      {/* Gauge background */}
                      <div className="absolute inset-x-0 h-1 rounded-full bg-foreground-800/50 overflow-hidden pointer-events-none">
                        <div
                          className={`h-full rounded-full transition-all duration-200 ${swapRate >= 0 ? 'bg-accent-500' : 'bg-secondary-500'}`}
                          style={{ width: `${((swapRate + 50) / 60) * 100}%` }}
                        />
                      </div>
                      {/* Range input overlaid */}
                      <input
                        type="range" min="-50" max="10" step="0.5" value={swapRate}
                        onChange={(e) => setSwapRate(parseFloat(e.target.value))}
                        className="absolute inset-0 w-full h-full appearance-none cursor-pointer bg-transparent z-10
                          [&::-webkit-slider-runnable-track]:bg-transparent
                          [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-[16px] [&::-webkit-slider-thumb]:h-[16px] [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-secondary-500 [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-[0_0_0_4px_rgba(255,255,255,0.04)] [&::-webkit-slider-thumb]:transition-shadow [&::-webkit-slider-thumb]:hover:shadow-[0_0_0_8px_rgba(255,255,255,0.08)]
                          [&::-moz-range-track]:bg-transparent [&::-moz-range-track]:h-1
                          [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:w-[16px] [&::-moz-range-thumb]:h-[16px] [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-white [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-secondary-500 [&::-moz-range-thumb]:cursor-pointer"
                      />
                    </div>
                    <div className="flex justify-between -mt-0.5">
                      <span className="text-[10px] text-foreground-600 font-mono">-$50</span>
                      <span className="text-[10px] text-foreground-600 font-mono">$10</span>
                    </div>
                  </div>

                  {/* Lot Size slider */}
                  <div>
                    <div className="flex items-center justify-between mb-2.5">
                      <span className="text-[11px] font-[700] text-foreground-300 uppercase tracking-[0.08em]">Lot Size</span>
                      <div className="flex items-baseline gap-1">
                        <span className="text-[20px] font-[750] text-foreground-100 leading-none font-mono tracking-tight">
                          {lotSize.toFixed(2)}
                        </span>
                        <span className="text-[11px] text-foreground-500 font-mono">lots</span>
                      </div>
                    </div>
                    <div className="relative h-[22px] flex items-center">
                      <div className="absolute inset-x-0 h-1 rounded-full bg-foreground-800/50 overflow-hidden pointer-events-none">
                        <div
                          className="h-full rounded-full bg-foreground-500 transition-all duration-200"
                          style={{ width: `${(lotSize / 5.0) * 100}%` }}
                        />
                      </div>
                      <input
                        type="range" min="0.01" max="5.0" step="0.01" value={lotSize}
                        onChange={(e) => setLotSize(parseFloat(e.target.value))}
                        className="absolute inset-0 w-full h-full appearance-none cursor-pointer bg-transparent z-10
                          [&::-webkit-slider-runnable-track]:bg-transparent
                          [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-[16px] [&::-webkit-slider-thumb]:h-[16px] [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-foreground-400 [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-[0_0_0_4px_rgba(255,255,255,0.04)] [&::-webkit-slider-thumb]:transition-shadow [&::-webkit-slider-thumb]:hover:shadow-[0_0_0_8px_rgba(255,255,255,0.08)]
                          [&::-moz-range-track]:bg-transparent [&::-moz-range-track]:h-1
                          [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:w-[16px] [&::-moz-range-thumb]:h-[16px] [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-white [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-foreground-400 [&::-moz-range-thumb]:cursor-pointer"
                      />
                    </div>
                    <div className="flex justify-between -mt-0.5">
                      <span className="text-[10px] text-foreground-600 font-mono">0.01</span>
                      <span className="text-[10px] text-foreground-600 font-mono">5.0</span>
                    </div>
                  </div>

                  {/* Days Held slider */}
                  <div>
                    <div className="flex items-center justify-between mb-2.5">
                      <span className="text-[11px] font-[700] text-foreground-300 uppercase tracking-[0.08em]">Days Held</span>
                      <div className="flex items-baseline gap-1">
                        <span className="text-[20px] font-[750] text-foreground-100 leading-none font-mono tracking-tight">
                          {daysHeld}
                        </span>
                        <span className="text-[11px] text-foreground-500 font-mono">days</span>
                      </div>
                    </div>
                    <div className="relative h-[22px] flex items-center">
                      <div className="absolute inset-x-0 h-1 rounded-full bg-foreground-800/50 overflow-hidden pointer-events-none">
                        <div
                          className="h-full rounded-full bg-foreground-500 transition-all duration-200"
                          style={{ width: `${(daysHeld / 60) * 100}%` }}
                        />
                      </div>
                      <input
                        type="range" min="0" max="60" step="1" value={daysHeld}
                        onChange={(e) => setDaysHeld(parseInt(e.target.value))}
                        className="absolute inset-0 w-full h-full appearance-none cursor-pointer bg-transparent z-10
                          [&::-webkit-slider-runnable-track]:bg-transparent
                          [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-[16px] [&::-webkit-slider-thumb]:h-[16px] [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-foreground-400 [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-[0_0_0_4px_rgba(255,255,255,0.04)] [&::-webkit-slider-thumb]:transition-shadow [&::-webkit-slider-thumb]:hover:shadow-[0_0_0_8px_rgba(255,255,255,0.08)]
                          [&::-moz-range-track]:bg-transparent [&::-moz-range-track]:h-1
                          [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:w-[16px] [&::-moz-range-thumb]:h-[16px] [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-white [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-foreground-400 [&::-moz-range-thumb]:cursor-pointer"
                      />
                    </div>
                    <div className="flex justify-between -mt-0.5">
                      <span className="text-[10px] text-foreground-600 font-mono">0</span>
                      <span className="text-[10px] text-foreground-600 font-mono">60</span>
                    </div>
                  </div>

                  {/* Wednesday Rollover toggle */}
                  <div className="flex items-center justify-between px-3 py-3 rounded-lg bg-foreground-900/40 border border-foreground-800/30">
                    <div>
                      <div className="text-[11px] font-[700] text-foreground-300 uppercase tracking-[0.08em]">Wed Rollover</div>
                      <div className="text-[11px] text-foreground-500 font-mono mt-0.5">
                        {hasTriple ? '3× swap included' : 'standard only'}
                      </div>
                    </div>
                    <button
                      onClick={() => setHasTriple(!hasTriple)}
                      className={`relative w-12 h-7 rounded-full transition-colors cursor-pointer ${
                        hasTriple ? 'bg-secondary-500' : 'bg-foreground-700'
                      }`}
                    >
                      <span className={`absolute top-0.5 w-6 h-6 bg-white rounded-full transition-all ${
                        hasTriple ? 'left-[22px]' : 'left-0.5'
                      }`} />
                    </button>
                  </div>
                </div>

                {/* Separator */}
                <div className="w-full h-px bg-foreground-800/30" />

                {/* Result — terminal readout */}
                <div className={`relative overflow-hidden rounded-xl p-5 ${isPositive ? 'bg-accent-500' : 'bg-secondary-500'}`}>
                  <div
                    className="absolute inset-0 opacity-[0.05]"
                    style={{
                      backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,255,255,0.4) 2px, rgba(255,255,255,0.4) 4px)',
                    }}
                  />
                  <div className="relative">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[10px] font-[700] text-white/50 uppercase tracking-[0.1em]">Swap Cost</span>
                      <span className="text-[10px] font-mono text-white/40">OUTPUT</span>
                    </div>
                    <div className="text-[46px] md:text-[50px] font-[800] text-white leading-none tracking-[-0.035em] font-mono">
                      {isPositive ? '+' : ''}${swapCost.toFixed(2)}
                    </div>
                    <div className="flex items-center gap-1 mt-2.5 text-[11px] text-white/50 font-mono tracking-tight">
                      <span>{swapRate >= 0 ? '+' : ''}{swapRate.toFixed(2)}/d</span>
                      <span className="text-white/25">·</span>
                      <span>{lotSize.toFixed(2)}L</span>
                      <span className="text-white/25">·</span>
                      <span>{daysHeld}d</span>
                      {hasTriple && <><span className="text-white/25">·</span><span>3×Wed</span></>}
                    </div>
                  </div>
                </div>

                {/* Cost dominance warning */}
                {daysHeld >= 5 && (
                  <div className="p-4 rounded-xl bg-secondary-500/8 border border-secondary-500/20">
                    <div className="flex items-start gap-2.5">
                      <div className="w-5 h-5 rounded-full bg-secondary-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-information-line text-[11px] text-secondary-400 w-4 h-4 flex items-center justify-center" />
                      </div>
                      <p className="text-[12px] leading-relaxed text-foreground-300">
                        <span className="font-[600] text-foreground-200">Heads up:</span> {swapStep.costDominanceNote}
                      </p>
                    </div>
                  </div>
                )}

              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}